import { useCallback, useMemo, useState } from 'react';
import {
	SelectValue,
	UseSelect,
	UseSelectParams,
} from 'src/components/primitives/Select/hooks/types';

/**
 * Utility hook for Radix controlled select components.
 * Allows for options with arbitrary values, as long as they have an ID field.
 * Mapping to and from the selected option is done internally to this hook.
 * Also controls the open state.
 */
export default function useSelect<TValue extends SelectValue>({
	defaultOpen,
	onSelectedValueChange,
	selectedValue,
	options,
}: UseSelectParams<TValue>): UseSelect<TValue> {
	const [open, setOpen] = useState(!!defaultOpen);

	const optionByID = useMemo(() => {
		const map = new Map<string, (typeof options)[number]>();

		for (const option of options) {
			map.set(getSelectValueId(option.value), option);
		}

		return map;
	}, [options]);

	// Get currently selected option.
	const selectedOption = useMemo(() => {
		if (selectedValue == null) {
			return;
		}

		return optionByID.get(getSelectValueId(selectedValue));
	}, [optionByID, selectedValue]);

	// Pass newly selected value to onValueChange.
	const onValueChange = useCallback(
		(newId: string) => {
			const newOption = options.find(
				(option) => getSelectValueId(option.value) === newId
			);

			if (newOption == null) {
				return;
			}

			onSelectedValueChange(newOption.value);
		},
		[onSelectedValueChange, options]
	);

	const selectOptions = useMemo(() => {
		return options.map((option) => {
			return {
				label: option.label,
				// Radix expects a string value
				value: getSelectValueId(option.value),
				original: option.value,
			};
		});
	}, [options]);

	return {
		open,
		onOpenChange: setOpen,
		onValueChange,
		value: selectedOption ? getSelectValueId(selectedOption.value) : undefined,
		selectOptions,
		selectedOption,
	};
}

function getSelectValueId<TValue extends SelectValue>(value: TValue) {
	return (typeof value === 'object' ? value.selectId : value).toString();
}
